import { AutoCompleteContainer, SelectionDetails } from "../addGlobalAttributes.styles";
import { Controller, useFormContext } from "react-hook-form";
import AutoComplete from "../../../Common/Autocomplete/Autocomplete";
import { useGetProductLinesQuery, useLazyGetProductLineQuery } from "features/api/globalsApi.ts";
import { GlobalAttributeFields } from "../forms/schema";
import { SelectDoorStyle, SelectProductLine } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { useEffect, useMemo } from "react";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import { sortGlobalByDescription } from "components/NewOrders/utils/NewOrderUtils.tsx";
import { models } from "types/api/viewModels.ts";
import { useParams } from "react-router";
import useInitOrderDetail from "hooks/useInitOrderDetail.ts";
import { getOrderAccountInfo } from "pages/OrderPages/utils/OrderDetailUtil.ts";

interface StepOneProps {
	isUpdate?: boolean;
	isCustomColor?: boolean;
	isCustomQuote?: boolean;
	customQuoteGlobals?: models["GlobalsViewModel"];
}

const StepOne = ({ isUpdate, isCustomColor, isCustomQuote, customQuoteGlobals }: StepOneProps) => {
	const updatedGlobalAttributes = useSelector((state: RootState) => state.newOrder.updatedGlobalAttributes);

	const dispatch = useDispatch();
	const params = useParams();
	const { orderDetail } = useInitOrderDetail(params.orderId);

	const customQuoteProductLine = customQuoteGlobals?.productLine;
	const customQuoteDoorStyle = customQuoteGlobals?.style;
	const [trigger, { data: productLine, isLoading: isLoadingProductLine }] = useLazyGetProductLineQuery();
	const orderAccountInfo = getOrderAccountInfo(orderDetail);
	const { data: productLines, isLoading: isLoadingProductLines } = useGetProductLinesQuery(
		{ accountId: orderAccountInfo?.accountId ?? undefined, billToId: orderDetail?.billToId ?? undefined },
		{
			skip: isUpdate
		}
	);

	const customColorEligibleProductLines = (productLines ?? []).filter((line) => line.customColorEligible);
	const { clearErrors, control, watch, setValue } = useFormContext<GlobalAttributeFields>();
	// Fetch the productLine only once for the lifecycle
	useEffect(() => {
		if (!isUpdate) {
			return;
		}

		const productLineId = updatedGlobalAttributes?.globals?.productLine?.id;

		if (productLineId && !isCustomQuote) {
			trigger(productLineId);
		}
		if (isCustomQuote && customQuoteProductLine?.id) {
			trigger(customQuoteProductLine.id);
		}
	}, [customQuoteProductLine, isCustomQuote, isUpdate, trigger, updatedGlobalAttributes?.globals?.productLine?.id]);

	// Get preselected doorStyle when initializing
	useEffect(() => {
		if (!isUpdate) {
			return;
		}

		const preselectedDoorStyle = productLine?.styles?.find(
			(style) => style.id === (customQuoteDoorStyle?.id ?? updatedGlobalAttributes?.globals?.style?.id)
		);

		if (preselectedDoorStyle) {
			setValue("doorStyle", preselectedDoorStyle);
		}
	}, [
		isUpdate,
		productLine?.styles,
		setValue,
		updatedGlobalAttributes?.globals?.style?.id,
		customQuoteDoorStyle?.id
	]);

	const selectedProductLine = watch("productLine");

	const doorStyleOptions = selectedProductLine?.styles ?? productLine?.styles ?? [];

	const customColorStyleOptions = doorStyleOptions.filter((style) => style.customColorEligible);
	const productLineOptions = useMemo(() => {
		if (isCustomColor) {
			return sortGlobalByDescription(customColorEligibleProductLines);
		} else if (isCustomQuote) {
			const customQuoteEligibleProductLines = (productLines ?? []).filter((pl) => pl.customQuoteEligible);
			return sortGlobalByDescription(customQuoteEligibleProductLines);
		}
		return sortGlobalByDescription(productLines ?? []);
	}, [customColorEligibleProductLines, isCustomColor, isCustomQuote, productLines]);
	return (
		<AutoCompleteContainer>
			{isUpdate ? (
				<SelectionDetails data-testid="global-attribute-modal-selection">
					{updatedGlobalAttributes?.globals?.productLine?.description}
				</SelectionDetails>
			) : (
				<Controller
					name="productLine"
					control={control}
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							required
							isLoading={isLoadingProductLine || isLoadingProductLines}
							value={value}
							disableClearable
							label={SelectProductLine}
							getOptionLabel={(option) => option?.description}
							onChange={(_event, value) => {
								clearErrors("productLine");
								onChange(value);
							}}
							options={productLineOptions}
							dataTestId="select-product-line"
							isError={!!error}
							errorText={error?.message}
						/>
					)}
				/>
			)}

			{(!selectedProductLine || doorStyleOptions.length !== 0) && (
				<Controller
					name="doorStyle"
					control={control}
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							required
							value={value}
							disableClearable
							isLoading={isLoadingProductLine || isLoadingProductLines}
							label={SelectDoorStyle}
							getOptionLabel={(option) => option?.description}
							disabled={!selectedProductLine && updatedGlobalAttributes === undefined && !isCustomQuote}
							onChange={(_event, value) => {
								if (isUpdate && !customQuoteGlobals) {
									const globalAttributesToBeUpdated: models["GlobalsViewModel"] = {
										...updatedGlobalAttributes?.globals,
										style: {
											code: value.code,
											description: value.description,
											id: value.id
										}
									};
									dispatch(
										newOrderActions.updateGlobalAttributes({
											...updatedGlobalAttributes,
											globals: globalAttributesToBeUpdated
										})
									);
								}

								clearErrors("doorStyle");

								onChange(value);
							}}
							options={
								customColorStyleOptions.length > 0 && isCustomColor
									? sortGlobalByDescription(customColorStyleOptions)
									: sortGlobalByDescription(doorStyleOptions)
							}
							dataTestId="select-door-style"
							isError={!!error}
							errorText={error?.message}
						/>
					)}
				/>
			)}
		</AutoCompleteContainer>
	);
};

export default StepOne;
